import type { ApiMe } from './wallet';
import type { ApiListingChain } from './chain';
import type { ApiPartnerItem } from './partner';
import type { Fuel, Gearbox, BodyType, CarAttributes } from './car';
import type { ApiMakeItem, ApiModelItem, ApiPaginationPayload } from './common';

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
}

export enum PayoutCurrency {
  GAC = 'GAC',
  TON = 'TON',
  USDT = 'USDT',
  // Settlement stablecoins of the EVM chains (multichain spec §2): a purchase records the token it
  // actually settled in, which is no longer always USDT.
  USDC = 'USDC',
  EURC = 'EURC',
}

export interface ApiGetListingsPayload extends ApiPaginationPayload {
  sellerId?: string;
  makeId?: string;
  modelId?: string;
  vin?: string;
  yearFrom?: number;
  yearTo?: number;
  priceFrom?: number;
  priceTo?: number;
  currency?: Currency;
  payoutCurrency?: PayoutCurrency;
  /** Only cars of this body, with this gearbox, on this fuel — one value each. */
  bodyType?: BodyType;
  gearbox?: Gearbox;
  fuel?: Fuel;
  active?: string;
}

// Derived buyer-facing availability (escrow + Buy Now), mirrors the API enum.
// `reserved` is temporary (a pending Buy Now purchase — the car returns to the
// feed if the payment fails); `sold` is final; `in_escrow` is an open escrow deal.
export enum ListingAvailability {
  Available = 'available',
  Reserved = 'reserved',
  Sold = 'sold',
  InEscrow = 'in_escrow',
}

export interface ApiListingItem extends CarAttributes {
  makeId: string;
  modelId: string;
  vin: string;
  year: number;
  color: string | null;
  mileage: number | null;
  price: number;
  currency: Currency;
  payoutCurrency: PayoutCurrency;
  description: string;
  images: string[];
  active: boolean;
  sellerId: string;
  id: string;
  createdAt: Date;
  updatedAt: Date;
  make: ApiMakeItem;
  model: ApiModelItem;
  seller: ApiMe;
  // Detail endpoint only, and absent when the dealer has no logist on the card.
  partners?: ApiPartnerItem[];
  // Optional: only the detail endpoint (GET /buyer/listings/:id) computes it.
  availability?: ListingAvailability;
  /**
   * Networks this car can be paid on before the caller's region is applied (multichain spec
   * §4.3/§6) — each with the seller's payout address in that chain's family. Detail endpoint only,
   * like `availability`.
   */
  availableChains?: ApiListingChain[];
}
